// packages/web/src/lib/fieldDay.ts — S5a: THE TECH'S DAY, READ ONCE.
//
// wf03's brief is "in a truck, in a basement, with gloves on", and the read
// pattern is half of that promise: a per-visit fan-out on a phone on a bad
// connection is the difference between a screen and a spinner (S15/S16, and
// the 2026-08-13 rate-limit incident that made the point in production).
//
// So both field screens — Today and the Job — share ONE read shape, built
// entirely out of the batched link read (`?include=links`, S15). Six
// sweeps, one per TYPE, never one per row:
//
//   hvac_visit        + [work_order]                  the day itself
//   hvac_work_order   + [wo_site, wo_client, equipment]  what each visit is for
//   hvac_site         + [client]                      where, and how to get in
//   hvac_client                                       who
//   hvac_note         + [note_work_order]             what landed (CV3)
//   hvac_source       + [source_work_order]           what landed (CV3)
//
// WHY THE JOB SCREEN USES THE SAME SWEEPS RATHER THAN `getRecord`. Two
// reasons, and the second is the one that decided it. First: the job screen
// has to answer "when were we last here", which is the site's visit history
// — that is the visit sweep and the work-order sweep, already in hand.
// Second: the `jobs` TAB has no id in it. A tab that means "the job I am on
// now" has to resolve which job that is, and that resolution is the day. One
// code path serves both `/jobs` and `/jobs/detail/:id`, and costs the same.
//
// THERE IS NO CLIENT-SIDE "MY VISITS" FILTER HERE, DELIBERATELY.
// `hvac_visit` and `hvac_work_order` are both in the Rexx pack's `readScope`
// with `scopedRoles: ['tech']` — row scope does the filtering in the server.
// A tech who can see another tech's visit is a Core bug to report, not a
// thing for this module to paper over (S5a spec §2).
import {
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type LinkRow,
  type RecordRow,
} from './api';

export const VISIT_TYPE = 'hvac_visit';
export const WO_TYPE = 'hvac_work_order';
export const SITE_TYPE = 'hvac_site';
export const CLIENT_TYPE = 'hvac_client';
export const EQUIPMENT_TYPE = 'hvac_equipment';
export const RATE_TYPE = 'hvac_rate';
// CV3 — what LANDED on the job: the two record types an approved capture
// or a spoken note becomes. Read here (two more type sweeps, nothing per
// row) so the job screen can show "On this job" from the same one read.
export const NOTE_TYPE = 'hvac_note';
export const SOURCE_TYPE = 'hvac_source';

/** A visit's master_detail parent, and whose visit it was. `tech_profile`
 *  is read so the job screen can say "· you" about a PAST visit as a fact
 *  (matched against GET /api/me/record) rather than as an inference from
 *  row scope — see the job screen's own header. */
const VISIT_LINKS = ['work_order', 'tech_profile'];
/** The three relationships a job screen renders: where, who, and what is
 *  broken. `equipment` is cardinality 'many' on the work order. */
const WO_LINKS = ['wo_site', 'wo_client', 'equipment'];
/** hvac_site's own master_detail parent. */
const SITE_LINKS = ['client'];
/** CV3: the one relationship each landing type reaches its job through —
 *  a note's note_work_order, a retained capture's source_work_order. */
const NOTE_LINKS = ['note_work_order'];
/** CV5 §4 adds the second link a retained RECORDING carries —
 *  `source_note_record`, which names the note its transcript became. Read in
 *  the same sweep (one more link type on a request that already runs), so the
 *  job screen can say "this note has a recording" and reach it without a
 *  single per-row fetch. */
const SOURCE_LINKS = ['source_work_order', 'source_note_record'];

/** Visit lifecycle states that mean the visit is behind you. */
const VISIT_CLOSED = new Set(['completed', 'no_access', 'rescheduled']);
/** Work-order lifecycle states that mean the job is behind everyone. */
const WO_CLOSED = new Set(['done', 'cancelled', 'deposited']);

/** The other end of a relationship, whichever side of the link row this
 *  record sits on. Same helper SiteDetail/ClientDetail already carry —
 *  duplicated rather than exported from one of them because a screen is not
 *  a library. */
export function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

/** Every other end of a cardinality-'many' relationship. */
export function linkedIds(links: LinkRow[], selfId: string, linkType: string): string[] {
  const out: string[] = [];
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) out.push(l.dst_id);
    else if (l.dst_id === selfId) out.push(l.src_id);
  }
  return out;
}

/** What the four sweeps came back with. `*Links` maps follow the S15
 *  contract exactly: a record id ABSENT from the map was not answered for,
 *  which is a different fact from an empty array. */
export interface FieldRead {
  visits: RecordRow[];
  visitLinks: Record<string, LinkRow[]>;
  workOrders: Map<string, RecordRow>;
  woLinks: Record<string, LinkRow[]>;
  sites: Map<string, RecordRow>;
  siteLinks: Record<string, LinkRow[]>;
  clients: Map<string, RecordRow>;
  /** CV3 — what landed: notes and retained captures, each carrying the one
   *  link that names its job. Filtered per job with the linkedId helpers. */
  notes: RecordRow[];
  noteLinks: Record<string, LinkRow[]>;
  sources: RecordRow[];
  sourceLinks: Record<string, LinkRow[]>;
  /** Records of any of the swept types the server would not answer LINKS
   *  for. Normally empty; never silently dropped (S16). */
  unanswered: number;
}

function byId(rows: RecordRow[]): Map<string, RecordRow> {
  return new Map(rows.map((r) => [r.id, r]));
}

/** THE ONE READ. Four requests' worth of types (each paging at the engine's
 *  own 200-row limit), in parallel, and nothing per row — plus, when the
 *  caller asks for what LANDED (`landed: true` — the Job screen's "On this
 *  job"), the two CV3 landing sweeps in the same Promise.all. Opt-in
 *  because Today renders none of it and the whole-walk request backstop
 *  (requestBudget.test.tsx) counts every read a screen does not use.
 *  Without the flag the landing arrays are empty and the two requests are
 *  never made. */
export async function readFieldDay(
  tenantId: string,
  opts: { landed?: boolean } = {}
): Promise<FieldRead> {
  const none = { records: [], linksByRecordId: {}, truncated: [], unreadable: [] };
  const [visits, orders, sites, clients, notes, sources] = await Promise.all([
    listAllRecordsWithLinks(tenantId, VISIT_TYPE, { linkTypes: VISIT_LINKS }),
    listAllRecordsWithLinks(tenantId, WO_TYPE, { linkTypes: WO_LINKS, linkLimit: 200 }),
    listAllRecordsWithLinks(tenantId, SITE_TYPE, { linkTypes: SITE_LINKS }),
    listAllRecordsByType(tenantId, CLIENT_TYPE),
    opts.landed ? listAllRecordsWithLinks(tenantId, NOTE_TYPE, { linkTypes: NOTE_LINKS }) : Promise.resolve(none),
    opts.landed ? listAllRecordsWithLinks(tenantId, SOURCE_TYPE, { linkTypes: SOURCE_LINKS }) : Promise.resolve(none),
  ]);
  return {
    visits: visits.records,
    visitLinks: visits.linksByRecordId,
    workOrders: byId(orders.records),
    woLinks: orders.linksByRecordId,
    sites: byId(sites.records),
    siteLinks: sites.linksByRecordId,
    clients: byId(clients),
    notes: notes.records,
    noteLinks: notes.linksByRecordId,
    sources: sources.records,
    sourceLinks: sources.linksByRecordId,
    unanswered:
      visits.unreadable.length +
      visits.truncated.length +
      orders.unreadable.length +
      orders.truncated.length +
      sites.unreadable.length +
      sites.truncated.length +
      notes.unreadable.length +
      notes.truncated.length +
      sources.unreadable.length +
      sources.truncated.length,
  };
}

/** One row of the day, with everything the two screens render already
 *  resolved. `null` on a join means the record was not in what the server
 *  answered — the screens say which truth that is rather than going blank. */
export interface FieldJob {
  visit: RecordRow;
  workOrder: RecordRow | null;
  site: RecordRow | null;
  client: RecordRow | null;
  /** scheduled_start, falling back to actual_start; null when neither is set
   *  or either is unparseable. A visit with no time is still the tech's
   *  visit — it sorts last rather than disappearing. */
  startsAt: Date | null;
  urgent: boolean;
  /** The one-line issue: the work order's own title. */
  issue: string;
}

function text(r: RecordRow | null, name: string): string {
  if (!r) return '';
  const v = r.fields[name];
  return typeof v === 'string' ? v.trim() : '';
}

function when(r: RecordRow): Date | null {
  const raw = text(r, 'scheduled_start') || text(r, 'actual_start');
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Shapes every readable visit into a row. Order: earliest first, and a
 *  visit with no time at all sorts to the end (it is not "midnight"). */
export function jobsFrom(read: FieldRead): FieldJob[] {
  const out: FieldJob[] = [];
  for (const visit of read.visits) {
    const vLinks = read.visitLinks[visit.id];
    const woId = vLinks === undefined ? null : linkedId(vLinks, visit.id, 'work_order');
    const workOrder = woId === null ? null : (read.workOrders.get(woId) ?? null);
    const wLinks = workOrder === null ? undefined : read.woLinks[workOrder.id];
    const siteId =
      workOrder === null || wLinks === undefined ? null : linkedId(wLinks, workOrder.id, 'wo_site');
    const site = siteId === null ? null : (read.sites.get(siteId) ?? null);
    // The work order names its client where the sheet carried one; the rest
    // resolve through the site's own client, which is the join the office
    // screens already make (see requestBudget's own note on the V6 import).
    const woClientId =
      workOrder === null || wLinks === undefined ? null : linkedId(wLinks, workOrder.id, 'wo_client');
    const sLinks = site === null ? undefined : read.siteLinks[site.id];
    const siteClientId = site === null || sLinks === undefined ? null : linkedId(sLinks, site.id, 'client');
    const clientId = woClientId ?? siteClientId;
    out.push({
      visit,
      workOrder,
      site,
      client: clientId === null ? null : (read.clients.get(clientId) ?? null),
      startsAt: when(visit),
      urgent: workOrder?.fields['urgent'] === true && !WO_CLOSED.has(workOrder.lifecycle_state ?? ''),
      issue: text(workOrder, 'title') || workOrder?.display_name || '',
    });
  }
  return out.sort((a, b) => {
    if (a.startsAt === null && b.startsAt === null) return 0;
    if (a.startsAt === null) return 1;
    if (b.startsAt === null) return -1;
    return a.startsAt.getTime() - b.startsAt.getTime();
  });
}

/** Same calendar day in the reader's own timezone — a tech's "today" is the
 *  day they are standing in, never UTC's. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** The day, in order. A visit with no time at all is INCLUDED only if it is
 *  otherwise open — dropping it would hide work; dating it would invent a
 *  fact. It sorts last and renders without a time. */
export function jobsToday(jobs: FieldJob[], now: Date): FieldJob[] {
  return jobs.filter((j) => {
    if (j.startsAt !== null) return isSameDay(j.startsAt, now);
    return !VISIT_CLOSED.has(j.visit.lifecycle_state ?? '');
  });
}

/** WHICH ONE IS "NOW". Two rules, in order, and both are facts rather than
 *  guesses:
 *    1. a visit the tech has already declared themselves on — `on_site`,
 *       then `en_route`. They said so; nothing beats that.
 *    2. otherwise the latest visit whose start time has passed and which is
 *       not behind them — the one they should be at.
 *  Null when the day has not started yet, which is the true answer at 7am. */
export function currentJob(jobs: FieldJob[], now: Date): FieldJob | null {
  for (const state of ['on_site', 'en_route']) {
    const declared = jobs.find((j) => j.visit.lifecycle_state === state);
    if (declared) return declared;
  }
  let best: FieldJob | null = null;
  for (const j of jobs) {
    if (j.startsAt === null) continue;
    if (VISIT_CLOSED.has(j.visit.lifecycle_state ?? '')) continue;
    if (j.startsAt.getTime() <= now.getTime()) best = j;
  }
  return best;
}

/** The name for a row: who, and where. Never blank — a name that could not
 *  be read says which truth that is (S15/S16's rule, carried onto the
 *  phone). */
export function jobTitle(job: FieldJob): string {
  const client = job.client?.display_name.trim() ?? '';
  const site = job.site?.display_name.trim() ?? '';
  if (client && site) return `${client} · ${site}`;
  if (site) return site;
  if (client) return client;
  if (job.workOrder) return job.workOrder.display_name;
  return job.visit.display_name;
}

/** 24-hour clock, tabular, the tech's own timezone. Empty for a visit with
 *  no time — the row says "no time set" rather than showing a false 00:00. */
export function clockTime(d: Date | null): string {
  if (d === null) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "Wed 14 Aug" — the day line under the Today title. */
export function dayLabel(d: Date): string {
  return d.toLocaleDateString('en-CA', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "Jun 22" — the last-visit line on the job screen. */
export function shortDate(d: Date): string {
  return d.toLocaleDateString('en-CA', { day: 'numeric', month: 'short' });
}

export { VISIT_CLOSED, WO_CLOSED };
